import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { startDebate, submitRound, getUsage, getCategories, getDifficulties, getDailyToday, startDailyDebate, getTodayStats, getProfileStats, generateRandomTopic, startDemoDebate, submitDemoRound, getRebecaHome, startSeoDebate } from "@/lib/api";
import { buildCompactShare, copyCompactShare, verdictFromScore, WIN_SCORE, ALMOST_SCORE } from "@/lib/share";
import { roundTimeLimit, roundLabel, isExpressDebate, debateMaxRounds } from "@/lib/debate";
import Header from "@/components/Header";
import ProGateModal from "@/components/ProGateModal";
import RebecaHomeCard from "@/components/RebecaHomeCard";

const AI_AVATAR =
  "https://static.prod-images.emergentagent.com/jobs/b9a6f8ac-ae9e-4cbf-9b6d-e590a64b44ee/images/6c99ee1aa365db3f6f9a5be21aaf930265acd3722b4b50b28b7389055d758d68.png";

const CATEGORY_COLORS = {
  vida_cotidiana: "#FFDE59",
  cultura_pop: "#A8FF76",
  comida: "#FF4C4C",
  deportes: "#7DD3FC",
  tecnologia: "#C084FC",
  filosofia: "#FB923C",
  absurdos: "#F9A8D4",
  argentina_profunda: "#6EE7B7",
  conspiranoia: "#FCD34D",
  dilemas_morales: "#F87171",
};

const CATEGORY_ICONS = {
  vida_cotidiana: "☕",
  cultura_pop: "🎬",
  comida: "🍕",
  deportes: "⚽",
  tecnologia: "🤖",
  filosofia: "🧠",
  absurdos: "🤪",
  argentina_profunda: "🧉",
  conspiranoia: "🕵️",
  dilemas_morales: "😈",
};

const DIFFICULTY_COLORS = {
  piola: "#A8FF76",
  pesada: "#FFDE59",
  implacable: "#FF4C4C",
};

const DIFFICULTY_EMOJI = {
  piola: "🙂",
  pesada: "😏",
  implacable: "😈",
};

const DEFAULT_DIFFICULTY = "pesada";
const DIFFICULTY_STORAGE_KEY = "convenceme_difficulty";

export default function GamePage({ user: initialUser, demoMode = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(initialUser);
  const [challengeContext, setChallengeContext] = useState(location.state?.challenge || null);
  const seoSlug = location.state?.seoSlug || null;
  const [debate, setDebate] = useState(null);
  const [argument, setArgument] = useState("");
  const [timeLeft, setTimeLeft] = useState(60);
  const [phase, setPhase] = useState("selecting"); // selecting | intro | playing | judging | transition | blocked
  const [usage, setUsage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [difficulties, setDifficulties] = useState([]);
  const [difficulty, setDifficulty] = useState(() => {
    try { return localStorage.getItem(DIFFICULTY_STORAGE_KEY) || DEFAULT_DIFFICULTY; }
    catch (_e) { return DEFAULT_DIFFICULTY; }
  });
  const [daily, setDaily] = useState(null);
  const [todayStats, setTodayStats] = useState(null);
  const [customTopic, setCustomTopic] = useState("");
  const [isCustomDebate, setIsCustomDebate] = useState(false);
  const [suggestedDifficulty, setSuggestedDifficulty] = useState(null);
  const [profileStats, setProfileStats] = useState(null);
  const [rebecaHome, setRebecaHome] = useState(null);
  const [gateOpen, setGateOpen] = useState(false);
  const [gateReason, setGateReason] = useState("limit");
  const [generatingTopic, setGeneratingTopic] = useState(false);
  const [preResult, setPreResult] = useState(null); // {debate, score, verdict, rebecaFinal}
  const [showMoreModes, setShowMoreModes] = useState(false);
  const intervalRef = useRef(null);
  const submittedRef = useRef(false);

  const loadMeta = async (shouldSetPhase = true) => {
    try {
      const [u, cats, diffs, d, stats, profileStats, rebeca] = await Promise.all([
        getUsage(),
        getCategories(),
        getDifficulties().catch(() => []),
        getDailyToday().catch(() => null),
        getTodayStats().catch(() => null),
        getProfileStats().catch(() => null),
        getRebecaHome().catch(() => null),
      ]);
      setUsage(u);
      setCategories(cats);
      setDifficulties(diffs);
      setDaily(d);
      setTodayStats(stats);
      setProfileStats(profileStats);
      setRebecaHome(rebeca);
      if (d?.completed) setShowMoreModes(true);
      if (profileStats?.suggested_difficulty) {
        setSuggestedDifficulty(profileStats.suggested_difficulty);
      }
      if (shouldSetPhase) {
        if (u && !u.is_pro && u.remaining <= 0 && (!d || d.completed)) {
          setGateReason("limit");
          setGateOpen(true);
          setPhase("blocked");
        } else {
          setPhase("selecting");
        }
      }
    } catch (_e) {
      // ignore
    }
  };

  const changeDifficulty = (id) => {
    setDifficulty(id);
    try { localStorage.setItem(DIFFICULTY_STORAGE_KEY, id); } catch (_e) {}
  };

  useEffect(() => {
    if (demoMode) {
      const incomingDemo = location.state?.debate;
      if (incomingDemo) {
        setDebate(incomingDemo);
        setTimeLeft(roundTimeLimit(incomingDemo, incomingDemo.current_round));
        setPhase("pregame");
        return undefined;
      }
      let active = true;
      (async () => {
        try {
          setPhase("intro");
          const d = await startDemoDebate();
          if (!active) return;
          setDebate(d);
          setTimeLeft(roundTimeLimit(d, d.current_round));
          setPhase("pregame");
        } catch (e) {
          const detail = e?.response?.data?.detail;
          toast.error(
            typeof detail === "string" && detail.includes("MongoDB")
              ? "MongoDB no está corriendo. Iniciá Docker o el servicio de MongoDB."
              : "No se pudo iniciar la demo."
          );
          navigate("/", { replace: true });
        }
      })();
      return () => {
        active = false;
      };
    }

    const incomingDebate = location.state?.debate;
    const incomingChallenge = location.state?.challenge;
    if (incomingDebate) {
      // Coming from ChallengePage / SeoPlayPage with a pre-started debate — jump straight to pregame
      setDebate(incomingDebate);
      setTimeLeft(roundTimeLimit(incomingDebate, incomingDebate.current_round));
      setPhase("pregame");
      if (incomingChallenge) setChallengeContext(incomingChallenge);
      loadMeta(false); // load metadata silently, don't override phase
    } else {
      loadMeta();
      // OAuth callback after SEO landing login — resume pending debate
      (async () => {
        let pendingSeo = null;
        try {
          pendingSeo = localStorage.getItem("pending_seo_slug");
          if (pendingSeo) localStorage.removeItem("pending_seo_slug");
        } catch (_e) {}
        if (!pendingSeo) return;
        submittedRef.current = false;
        setArgument("");
        setPhase("intro");
        try {
          const d = await startSeoDebate(pendingSeo, difficulty);
          setDebate(d);
          setTimeLeft(roundTimeLimit(d, d.current_round));
          setPhase("pregame");
        } catch (e) {
          const detail = e?.response?.data?.detail;
          if (detail === "DAILY_LIMIT_REACHED") {
            setGateReason("limit");
            setGateOpen(true);
            setPhase("blocked");
          } else {
            toast.error("No se pudo iniciar el debate SEO.");
            setPhase("selecting");
          }
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerateTopic = async () => {
    setGeneratingTopic(true);
    try {
      const { topic } = await generateRandomTopic();
      setCustomTopic(topic);
    } catch (_e) {
      toast.error("No se pudo generar el tema.");
    } finally {
      setGeneratingTopic(false);
    }
  };

  const startCustomDebate = async () => {
    const topic = customTopic.trim();
    if (!topic) return;
    submittedRef.current = false;
    setArgument("");
    setIsCustomDebate(true);
    setCustomTopic("");
    setPhase("intro");
    try {
      const d = await startDebate(null, difficulty, topic);
      setDebate(d);
      setTimeLeft(roundTimeLimit(d, d.current_round));
      setPhase("pregame");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "DAILY_LIMIT_REACHED") {
        setGateReason("limit"); setGateOpen(true); setPhase("blocked");
      } else {
        toast.error("No se pudo iniciar el debate.");
        setPhase("selecting");
      }
    } finally {
      setIsCustomDebate(false);
    }
  };

  const pickCategory = async (categoryId) => {
    // If selected category is locked for this user, open gate modal.
    if (categoryId) {
      const cat = categories.find((c) => c.id === categoryId);
      if (cat && !cat.free && !user?.is_pro) {
        setGateReason("category");
        setGateOpen(true);
        return;
      }
    }
    submittedRef.current = false;
    setArgument("");
    setPhase("intro");
    try {
      const d = await startDebate(categoryId, difficulty);
      setDebate(d);
      setTimeLeft(roundTimeLimit(d, d.current_round));
      setPhase("pregame");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "DAILY_LIMIT_REACHED") {
        setGateReason("limit");
        setGateOpen(true);
        setPhase("blocked");
      } else if (detail === "CATEGORY_LOCKED") {
        setGateReason("category");
        setGateOpen(true);
        setPhase("selecting");
      } else {
        toast.error("No se pudo iniciar el debate.");
        setPhase("selecting");
      }
    }
  };

  const playDaily = async () => {
    submittedRef.current = false;
    setArgument("");
    setPhase("intro");
    try {
      const d = await startDailyDebate();
      setDebate(d);
      if (d.completed) {
        // Already finished today's daily — go to its result page.
        navigate(`/result/${d.debate_id}`, { replace: true, state: { result: d, user } });
        return;
      }
      setTimeLeft(roundTimeLimit(d, d.current_round));
      setPhase("pregame");
    } catch (_e) {
      toast.error("No se pudo iniciar el reto del día.");
      setPhase("selecting");
    }
  };

  useEffect(() => {
    if (demoMode || !location.state?.autoDaily) return;
    if (phase !== "selecting" || !daily || daily.completed) return;
    window.history.replaceState({}, document.title);
    playDaily();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoMode, location.state, phase, daily]);

  const submitCurrentRound = async (silent = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    if (intervalRef.current) clearInterval(intervalRef.current);
    setPhase("judging");
    try {
      const updated = demoMode
        ? await submitDemoRound(debate.debate_id, argument)
        : await submitRound(debate.debate_id, argument);
      setDebate(updated);
      if (updated.completed) {
        if (updated.is_daily) {
          try {
            const [ps, rebeca] = await Promise.all([getProfileStats(), getRebecaHome()]);
            setProfileStats(ps);
            setRebecaHome(rebeca);
            setUser((u) =>
              u
                ? {
                    ...u,
                    play_streak: ps.play_streak,
                    win_streak: ps.win_streak,
                    streak: ps.play_streak,
                  }
                : u
            );
          } catch (_e) {
            /* ignore */
          }
        }
        // Mejora 5: pantalla pre-resultado con presión social
        const finalRound = updated.rounds?.[updated.rounds.length - 1];
        setPreResult({
          debate: updated,
          score: updated.total_score ?? 0,
          verdict: verdictFromScore(updated.total_score ?? 0, updated.verdict_message),
          rebecaFinal: finalRound?.ai_counter || "",
          rebecaFeedback: updated.rebeca_feedback || "",
          challenge: challengeContext,
        });
        setPhase("pre_result");
        return;
      }
      // Show REBECA's counter with a manual continue button — timer does NOT auto-start.
      setArgument("");
      setPhase("round_summary");
    } catch (e) {
      if (!silent) {
        const detail = e?.response?.data?.detail || "";
        if (detail.includes("AI_UNAVAILABLE") || e?.response?.status === 503) {
          toast.error("⚠️ La IA no está disponible ahora mismo. Intentá de nuevo en unos segundos.");
        } else {
          toast.error("Error al enviar la ronda.");
        }
      }
      setPhase("playing");
      submittedRef.current = false;
    }
  };

  const startNextRound = () => {
    if (!debate) return;
    setTimeLeft(roundTimeLimit(debate, debate.current_round));
    submittedRef.current = false;
    setPhase("playing");
  };

  // Auto-transition from pregame to playing after 3s (punto 2)
  useEffect(() => {
    if (phase !== "pregame" || !debate) return;
    const t = setTimeout(() => setPhase("playing"), 3200);
    return () => clearTimeout(t);
  }, [phase, debate]);

  useEffect(() => {
    if (phase !== "playing") return;
    intervalRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(intervalRef.current);
          submitCurrentRound(true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Mejora 3: countdown al daily
  const [dailyCountdown, setDailyCountdown] = useState("");
  useEffect(() => {
    const update = () => {
      const now = new Date();
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      const diff = midnight - now;
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setDailyCountdown(`${h}h ${m}m`);
    };
    update();
    const t = setInterval(update, 60000);
    return () => clearInterval(t);
  }, []);

  const lowTime = timeLeft <= 10;
  const mm = String(Math.floor(timeLeft / 60)).padStart(2, "0");
  const ss = String(timeLeft % 60).padStart(2, "0");

  const onUpgraded = async () => {
    setGateOpen(false);
    setUser((u) => (u ? { ...u, is_pro: true } : u));
    // Refresh meta so picker re-renders without locks.
    await loadMeta();
  };

  const lastCounter = debate?.rounds?.length
    ? debate.rounds[debate.rounds.length - 1]?.ai_counter
    : null;

  const currentRound = debate?.current_round || 1;

  // Confidence bar helpers (punto 3 y 5)
  const completedRounds = debate?.rounds?.filter(r => r.score != null) || [];
  const runningAvg = completedRounds.length
    ? Math.round(completedRounds.reduce((s, r) => s + r.score, 0) / completedRounds.length)
    : null;
  const rebecaConfidence = runningAvg != null ? Math.max(0, 100 - runningAvg) : 100;
  const confidenceColor = rebecaConfidence > 65 ? "#FF4C4C" : rebecaConfidence > 35 ? "#FFDE59" : "#A8FF76";
  const confidenceLabel = rebecaConfidence > 65 ? "REBECA confiada" : rebecaConfidence > 35 ? "Debate parejo" : "REBECA nerviosa 😬";

  const handleCompactShare = async (result) => {
    try {
      await copyCompactShare({
        score: result.score,
        stance: result.debate?.stance,
        isDaily: result.debate?.is_daily,
        dailyDate: result.debate?.daily_date,
      });
      toast.success("Resultado copiado — pegalo en WhatsApp o stories");
    } catch (_e) {
      toast.error("No se pudo copiar el resultado.");
    }
  };

  if (demoMode && (phase === "selecting" || phase === "intro")) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <div className="font-display text-2xl md:text-3xl uppercase text-center mb-2">
          REBECA está preparando tu demo...
        </div>
        <div className="font-mono-cm text-xs uppercase tracking-widest text-[#101010]/50">
          Sin login · 1 ronda express · reto del día
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6 md:py-10">
      {demoMode ? (
        <div className="max-w-4xl mx-auto flex items-center justify-between mb-6">
          <Link to="/" className="font-display text-xl md:text-2xl uppercase tracking-tighter">
            Conven<span className="text-[#FF4C4C]">ce</span>me
          </Link>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/50">
              Modo demo
            </span>
            <button
              type="button"
              onClick={() => {
                window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google`;
              }}
              className="cm-press text-xs font-mono-cm uppercase tracking-widest border-2 border-[#101010] bg-[#FFDE59] px-3 py-1.5 rounded-md cm-shadow-sm"
            >
              Entrar
            </button>
          </div>
        </div>
      ) : (
        <Header user={user} />
      )}

      <div className="max-w-4xl mx-auto">
        {/* Mejora 7: banner top semanal */}
        {usage?.league_tier && phase === "selecting" && (
          <motion.div
            initial={{ y: -8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mb-3 bg-white border-4 border-[#101010] rounded-xl cm-shadow px-4 py-3 flex items-center gap-3 flex-wrap"
            data-testid="league-banner"
          >
            <span className="text-xl">
              {usage.league_tier === "oro" ? "🥇" : usage.league_tier === "plata" ? "🥈" : "🥉"}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-display uppercase text-sm md:text-base">
                Liga {usage.league_tier_label}
                {usage.league_rank ? (
                  <> · #{usage.league_rank}</>
                ) : (
                  <> · jugá para rankear</>
                )}
              </div>
              <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60">
                {usage.league_score > 0 ? `${usage.league_score} pts esta semana` : "Sumá puntos esta semana"}
                {usage.league_days_left != null && ` · ${usage.league_days_left} día${usage.league_days_left !== 1 ? "s" : ""} restantes`}
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/leaderboard", { state: { tab: "league" } })}
              className="cm-press shrink-0 bg-[#101010] text-[#FFDE59] border-2 border-[#101010] rounded-lg px-3 py-1 font-display uppercase text-xs"
            >
              Ver liga →
            </button>
          </motion.div>
        )}

        {challengeContext && phase !== "selecting" && (
          <motion.div
            initial={{ y: -8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mb-4 bg-[#101010] border-4 border-[#101010] rounded-xl cm-shadow px-4 py-3 flex items-center gap-3"
            data-testid="challenge-fair-banner"
          >
            <span className="text-xl">⚔️</span>
            <div className="flex-1 min-w-0">
              <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#FFDE59]">
                Desafío de {challengeContext.challenger_name?.split(" ")[0]} · meta {challengeContext.challenger_score}/100
              </div>
              {(challengeContext.cached_rounds_count > 0) && (
                <div className="font-mono-cm text-[10px] uppercase tracking-widest text-white/60 mt-0.5">
                  ⚖️ Mismos contraataques de REBECA · comparación justa
                </div>
              )}
            </div>
          </motion.div>
        )}

        {usage && (
          <div className="mb-4 flex items-center justify-between text-xs font-mono-cm uppercase tracking-widest text-[#101010]/70">
            <span data-testid="usage-strip">
              {usage.is_pro
                ? "PRO · Debates ilimitados"
                : `Hoy: ${usage.used_today}/${usage.daily_limit} debates gratis`}
            </span>
            {debate && phase !== "selecting" && (
              <span className="font-mono-cm flex items-center gap-2" data-testid="game-meta-strip">
                <span>{roundLabel(debate, currentRound)} · {debate.category_label}</span>
                <span className="inline-flex items-center gap-1 border-2 border-[#101010] rounded px-1.5 py-0.5 bg-white" data-testid="active-difficulty-chip">
                  {DIFFICULTY_EMOJI[debate.difficulty] || "🤖"} {(debate.difficulty_label || "").replace("REBECA ", "")}
                </span>
              </span>
            )}
          </div>
        )}

        <AnimatePresence mode="wait">
          {phase === "selecting" && !demoMode && (
            <motion.div
              key="selecting"
              initial={{ y: 14, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ opacity: 0 }}
              data-testid="category-picker"
            >
              {rebecaHome && (
                <RebecaHomeCard
                  message={rebecaHome.message}
                  ctaHint={rebecaHome.cta_hint}
                  mood={rebecaHome.mood}
                  compact
                  testId="game-rebeca-home-card"
                />
              )}

              <h2 className="font-display uppercase text-3xl md:text-5xl leading-tight mb-2">
                {daily && !daily.completed ? (
                  <>El <span className="text-[#FF4C4C]">reto de hoy</span></>
                ) : (
                  <>Elegí tu <span className="text-[#FF4C4C]">próximo</span> debate</>
                )}
              </h2>

              {/* Reto del día — protagonista */}
              {daily && (
                <motion.div
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.4 }}
                  className={`mb-5 border-4 border-[#101010] rounded-2xl cm-shadow overflow-hidden ${
                    daily.completed ? "bg-white" : "bg-gradient-to-br from-[#FFDE59] via-[#FFDE59] to-[#FF4C4C]"
                  }`}
                  data-testid="daily-card"
                >
                  <div className={`px-5 py-3 flex items-center justify-between gap-3 ${daily.completed ? "bg-[#101010]" : "bg-[#101010]"}`}>
                    <div className="inline-flex items-center gap-2">
                      <span className="text-lg">🔥</span>
                      <span className="font-mono-cm text-[10px] font-bold uppercase tracking-widest text-[#FFDE59]">
                        Reto del día · {daily.date}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="font-mono-cm text-[10px] uppercase tracking-widest text-white/80" data-testid="daily-players">
                        {daily.players_today} jugaron hoy
                      </span>
                      {dailyCountdown && (
                        <div className="font-mono-cm text-[9px] uppercase tracking-widest text-white/50 mt-0.5">
                          ⏳ Quedan {dailyCountdown}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="p-5 md:p-6">
                    <p className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/50 mb-2">
                      Todos juegan la misma postura
                    </p>
                    <p className="font-display uppercase text-xl md:text-3xl leading-tight mb-5" data-testid="daily-stance">
                      &ldquo;{daily.stance}&rdquo;
                    </p>

                    {daily.completed ? (
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <button
                          onClick={() => navigate(`/result/${daily.debate_id}`)}
                          data-testid="daily-view-result-button"
                          className="cm-press bg-[#A8FF76] text-[#101010] border-4 border-[#101010] rounded-xl px-5 py-3 font-display uppercase text-sm md:text-base cm-shadow-sm"
                        >
                          ✅ Hoy: {daily.total_score}/100 — Ver resultado
                        </button>
                        <span className="font-mono-cm text-xs uppercase tracking-widest text-[#101010]/60">
                          Volvé mañana para el próximo reto
                        </span>
                      </div>
                    ) : (
                      <>
                        {(profileStats?.play_streak > 0 || !profileStats?.played_daily_today) && (
                          <div
                            className={`mb-4 border-4 border-[#101010] rounded-xl px-4 py-3 ${
                              profileStats?.play_streak > 0 ? "bg-[#FF4C4C] text-white" : "bg-white"
                            }`}
                            data-testid="daily-streak-banner"
                          >
                            <p className="font-mono-cm text-[10px] uppercase tracking-widest opacity-80 mb-1">
                              {profileStats?.play_streak > 0 ? "Racha activa" : "Empezá tu racha"}
                            </p>
                            <p className="font-display uppercase text-base md:text-lg leading-tight">
                              {profileStats?.play_streak > 0
                                ? `🔥 ${profileStats.play_streak} día${profileStats.play_streak !== 1 ? "s" : ""} seguidos — jugá hoy para mantenerla`
                                : "1 reto por día · ganá o perdé, la racha cuenta igual"}
                            </p>
                          </div>
                        )}
                        <button
                          onClick={playDaily}
                          data-testid="daily-play-button"
                          className="cm-press w-full bg-[#101010] text-[#FFDE59] border-4 border-[#101010] rounded-xl px-6 py-5 font-display uppercase text-xl md:text-2xl cm-shadow"
                        >
                          🔥 Aceptar desafío
                        </button>
                        <p className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60 text-center mt-3">
                          Express · 1 ronda · 60 seg · no consume cuota
                        </p>
                      </>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Banner estadística del día */}
              {todayStats && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 bg-[#101010] border-4 border-[#101010] rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap"
                >
                  <span className="text-xl">📊</span>
                  <div className="flex-1 min-w-0">
                    {todayStats.win_rate !== null ? (
                      <span className="font-mono-cm text-xs uppercase tracking-widest text-white">
                        Hoy solo el{" "}
                        <span className="text-[#FF4C4C] font-bold text-sm">{todayStats.win_rate}%</span>
                        {" "}le ganó a REBECA
                        {todayStats.total_debates > 0 && (
                          <span className="text-white/40"> · {todayStats.total_debates} debate{todayStats.total_debates !== 1 ? "s" : ""}</span>
                        )}
                      </span>
                    ) : (
                      <span className="font-mono-cm text-xs uppercase tracking-widest text-white/70">
                        Nadie jugó todavía hoy — sé el primero
                      </span>
                    )}
                  </div>
                  <span className="font-display text-[#FFDE59] text-sm uppercase">¿Sos del grupo?</span>
                </motion.div>
              )}

              {!daily?.completed && (
                <p className="font-mono-cm text-[11px] uppercase tracking-widest text-[#101010]/50 mb-4 text-center">
                  ¿Querés otro modo? Abrí más opciones abajo
                </p>
              )}

              <button
                type="button"
                onClick={() => setShowMoreModes((v) => !v)}
                data-testid="toggle-more-modes"
                className="cm-press w-full mb-4 bg-white border-4 border-[#101010] rounded-xl px-5 py-3 font-display uppercase text-sm md:text-base cm-shadow-sm flex items-center justify-between"
              >
                <span>{showMoreModes ? "Ocultar más modos" : "Más modos de juego"}</span>
                <span className="font-mono-cm text-xs opacity-60">{showMoreModes ? "▲" : "▼"}</span>
              </button>

              {showMoreModes && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4 mb-4"
                  data-testid="more-modes-panel"
                >
              <p className="text-sm md:text-base text-[#101010]/70">
                Modo completo: 3 rondas por categoría, tema libre y nivel de REBECA.
              </p>

              {/* Difficulty selector */}
              {difficulties.length > 0 && (
                <div className="mb-6" data-testid="difficulty-selector">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono-cm text-[10px] md:text-xs uppercase tracking-widest text-[#101010]/60">
                      Nivel de REBECA
                    </div>
                    <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/40">
                      Daily challenge usa modo pesada
                    </div>
                  </div>
                  {suggestedDifficulty && (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-3 flex items-center gap-2 bg-[#A8FF76] border-4 border-[#101010] rounded-xl px-4 py-2.5 cm-shadow-sm"
                      data-testid="level-up-banner"
                    >
                      <span className="text-xl">💪</span>
                      <div className="flex-1 min-w-0">
                        <span className="font-mono-cm text-[11px] uppercase tracking-widest text-[#101010]">
                          Basado en tu historial, REBECA recomienda que subas al nivel{" "}
                          <strong className="font-bold">
                            {difficulties.find((d) => d.id === suggestedDifficulty)?.label?.replace("REBECA ", "") || suggestedDifficulty}
                          </strong>
                        </span>
                      </div>
                      <button
                        onClick={() => changeDifficulty(suggestedDifficulty)}
                        className="cm-press shrink-0 bg-[#101010] text-[#A8FF76] border-2 border-[#101010] rounded-lg px-3 py-1 font-mono-cm text-[10px] uppercase tracking-widest"
                      >
                        Subir →
                      </button>
                    </motion.div>
                  )}
                  <div className="grid grid-cols-3 gap-3 pb-1 pr-1">
                    {difficulties.map((d) => {
                      const active = difficulty === d.id;
                      const isSuggested = d.id === suggestedDifficulty;
                      const color = DIFFICULTY_COLORS[d.id] || "#FFDE59";
                      return (
                        <button
                          key={d.id}
                          onClick={() => changeDifficulty(d.id)}
                          data-testid={`difficulty-${d.id}`}
                          aria-pressed={active}
                          className={`cm-press relative border-4 border-[#101010] rounded-xl p-3 text-left ${
                            active ? "cm-shadow-sm" : "bg-white"
                          }`}
                          style={active ? { background: color } : {}}
                        >
                          {isSuggested && !active && (
                            <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#A8FF76] border-2 border-[#101010] px-2 py-0.5 rounded font-mono-cm text-[8px] uppercase tracking-widest whitespace-nowrap z-10">
                              💪 Recomendado
                            </span>
                          )}
                          <div className="flex flex-col items-center justify-center text-center gap-1 py-1">
                            <span className="text-2xl leading-none">{DIFFICULTY_EMOJI[d.id] || "🤖"}</span>
                            <span className="font-display uppercase text-xs sm:text-sm leading-tight">
                              {d.label.replace("REBECA ", "")}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Traé tu tema */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="border-4 border-[#101010] rounded-xl cm-shadow bg-[#FFDE59] p-4 md:p-5 mb-4"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl shrink-0">✏️</span>
                  <div>
                    <div className="font-display uppercase text-base sm:text-lg leading-tight">Traé tu propio tema</div>
                    <div className="font-mono-cm text-[9px] sm:text-[10px] uppercase tracking-widest text-[#101010]/50 leading-tight">
                      REBECA toma la postura contraria y la defiende
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={customTopic}
                    onChange={(e) => setCustomTopic(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && customTopic.trim() && startCustomDebate()}
                    placeholder="Ej: que el home office es mejor que la oficina..."
                    maxLength={120}
                    className="flex-1 border-4 border-[#101010] rounded-xl px-4 py-3 font-medium text-sm bg-white focus:outline-none transition-colors"
                  />
                  <button
                    onClick={startCustomDebate}
                    disabled={!customTopic.trim()}
                    className="cm-press shrink-0 bg-[#FF4C4C] text-white border-4 border-[#101010] rounded-xl px-4 py-3 font-display uppercase text-sm cm-shadow-sm disabled:bg-[#101010]/20 disabled:text-[#101010]/40 disabled:border-[#101010]/30 disabled:shadow-none disabled:cursor-not-allowed transition-colors"
                  >
                    Debatir →
                  </button>
                </div>
                {/* Mejora 4: generar tema absurdo */}
                <button
                  onClick={handleGenerateTopic}
                  disabled={generatingTopic}
                  className="cm-press w-full border-2 border-[#101010]/40 border-dashed rounded-xl px-4 py-2.5 font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/70 hover:border-[#101010] hover:text-[#101010] hover:bg-white/50 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {generatingTopic
                    ? "⏳ Generando tema absurdo..."
                    : "🎲  No tengo tema — generá uno absurdo al azar"}
                </button>
              </motion.div>

              {/* Separador */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-0.5 bg-[#101010]/15" />
                <span className="font-mono-cm text-[11px] uppercase tracking-widest text-[#101010]/30">o elegí una categoría</span>
                <div className="flex-1 h-0.5 bg-[#101010]/15" />
              </div>

              <button
                onClick={() => pickCategory(null)}
                data-testid="category-random"
                className="cm-press w-full mb-5 bg-[#101010] text-[#FFFDF5] border-4 border-[#101010] rounded-xl px-5 py-4 font-display uppercase text-lg md:text-xl cm-shadow flex items-center justify-between"
              >
                <span>🎲 Aleatoria</span>
                <span className="font-mono-cm text-xs opacity-70">Sorpresa</span>
              </button>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-5">
                {categories.map((c) => {
                  const locked = !c.free && !user?.is_pro;
                  const color = CATEGORY_COLORS[c.id] || "#FFDE59";
                  const icon = CATEGORY_ICONS[c.id] || "💭";
                  return (
                    <button
                      key={c.id}
                      onClick={() => pickCategory(c.id)}
                      data-testid={`category-${c.id}`}
                      className={`cm-press relative text-left border-4 border-[#101010] rounded-xl p-4 md:p-5 cm-shadow ${
                        locked ? "bg-[#F0EDE1]" : ""
                      }`}
                      style={!locked ? { background: color } : {}}
                    >
                      <div className="text-3xl mb-2">{icon}</div>
                      <div className="font-display uppercase text-lg md:text-xl leading-tight">
                        {c.label}
                      </div>
                      <div className="font-mono-cm text-[10px] uppercase tracking-widest mt-1 opacity-70">
                        {c.free ? "Gratis" : locked ? "PRO" : "Disponible"}
                      </div>
                      {locked && (
                        <span
                          className="absolute top-2 right-2 font-mono-cm text-[10px] uppercase tracking-widest bg-[#FF4C4C] text-white border-2 border-[#101010] px-1.5 py-0.5 rounded"
                          data-testid={`category-${c.id}-lock-badge`}
                        >
                          🔒 PRO
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
                </motion.div>
              )}

            </motion.div>
          )}

          {phase === "blocked" && (
            <motion.div
              key="blocked"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
              data-testid="game-blocked"
            >
              <div className="font-display uppercase text-3xl mb-3">Sin debates por hoy</div>
              <button
                onClick={() => { setGateReason("limit"); setGateOpen(true); }}
                data-testid="open-gate-button"
                className="cm-press bg-[#FF4C4C] text-white border-4 border-[#101010] rounded-xl px-6 py-3 font-display uppercase cm-shadow"
              >
                Desbloquear PRO
              </button>
            </motion.div>
          )}

          {phase === "intro" && (
            <motion.div
              key="intro"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-20 font-display uppercase text-2xl md:text-3xl"
              data-testid="game-loading"
            >
              {isCustomDebate
                ? "REBECA está armando su postura contraria..."
                : "REBECA está eligiendo una postura ridícula..."}
            </motion.div>
          )}

          {/* Punto 2: Intro taunt de REBECA */}
          {phase === "pregame" && debate && (
            <motion.div
              key="pregame"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              className="flex flex-col items-center justify-center py-16 gap-8"
              data-testid="game-pregame"
            >
              <div className="w-full max-w-xl border-4 border-[#101010] rounded-2xl cm-shadow bg-[#FF4C4C] p-6 md:p-8 flex items-start gap-5">
                <img src={AI_AVATAR} alt="REBECA" className="w-20 h-20 shrink-0 object-contain" />
                <div>
                  <div className="font-mono-cm text-[10px] uppercase tracking-widest text-white/80 mb-2">
                    REBECA dice...
                  </div>
                  <p className="font-display uppercase text-xl md:text-2xl leading-tight text-white">
                    "{debate.rebeca_intro || "Che, avisame cuando estés listo."}"
                  </p>
                </div>
              </div>
              <div className="text-center">
                <p className="font-mono-cm text-xs uppercase tracking-widest text-[#101010]/50 mb-4">
                  El debate arranca en un momento...
                </p>
                <button
                  onClick={() => setPhase("playing")}
                  className="cm-press w-full sm:w-auto bg-[#101010] text-white border-4 border-[#101010] rounded-xl px-6 py-4 font-display uppercase text-lg cm-shadow"
                >
                  Estoy listo, dale →
                </button>
              </div>
            </motion.div>
          )}

          {/* Mejora 5: pantalla pre-resultado con presión social */}
          {phase === "pre_result" && preResult && (
            <motion.div
              key="pre_result"
              initial={{ scale: 0.93, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-10 gap-6 text-center"
            >
              {/* Veredicto grande */}
              <div className={`w-full border-4 border-[#101010] rounded-2xl cm-shadow p-6 ${preResult.score >= WIN_SCORE ? "bg-[#A8FF76]" : preResult.score >= ALMOST_SCORE ? "bg-[#FFDE59]" : "bg-[#FF4C4C]"}`}>
                <div className="font-display text-[4.5rem] sm:text-[6rem] leading-none tabular-nums">
                  {preResult.score}
                  <span className="text-2xl sm:text-3xl opacity-50">/100</span>
                </div>
                <div className="font-display uppercase text-2xl mt-1">
                  {preResult.verdict}
                </div>
                {preResult.score >= ALMOST_SCORE && preResult.score < WIN_SCORE && (
                  <p className="font-mono-cm text-xs uppercase tracking-widest text-[#101010]/70 mt-3">
                    Te faltaron {WIN_SCORE - preResult.score} pts para ganarle · REBECA sigue en su postura
                  </p>
                )}
                {preResult.debate?.is_daily && profileStats?.play_streak > 0 && (
                  <p className="font-mono-cm text-xs uppercase tracking-widest text-[#101010]/80 mt-3 pt-3 border-t border-[#101010]/20">
                    🔥 Racha de reto: {profileStats.play_streak} día{profileStats.play_streak !== 1 ? "s" : ""} · volvé mañana
                  </p>
                )}
              </div>

              {/* Lo que dijo REBECA al final */}
              {preResult.rebecaFinal && (
                <div className="w-full bg-[#101010] border-4 border-[#101010] rounded-xl cm-shadow p-5 flex items-start gap-4 text-left">
                  <img src={AI_AVATAR} alt="REBECA" className="w-12 h-12 shrink-0 object-contain" />
                  <div className="min-w-0">
                    <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#FF4C4C] mb-1">
                      {preResult.score >= WIN_SCORE ? "REBECA admitió derrota" : "REBECA cierra el debate"}
                    </div>
                    <p className="text-white font-medium text-sm md:text-base italic leading-relaxed">
                      &ldquo;{preResult.rebecaFinal}&rdquo;
                    </p>
                    {preResult.rebecaFeedback && (
                      <p className="text-white/75 text-sm mt-3 pt-3 border-t border-white/20">
                        💡 {preResult.rebecaFeedback}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Botones: compartir primero, resultado después */}
              <div className="w-full space-y-3">
                <button
                  type="button"
                  onClick={() => handleCompactShare(preResult)}
                  data-testid="compact-share-button"
                  className="cm-press w-full flex items-center justify-center gap-2 bg-[#FFDE59] text-[#101010] border-4 border-[#101010] rounded-xl px-6 py-4 font-display uppercase text-lg cm-shadow"
                >
                  <span>📋</span> Copiar resultado
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    buildCompactShare({
                      score: preResult.score,
                      stance: preResult.debate?.stance,
                      isDaily: preResult.debate?.is_daily,
                      dailyDate: preResult.debate?.daily_date,
                    })
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cm-press w-full flex items-center justify-center gap-2 bg-[#25D366] text-white border-4 border-[#101010] rounded-xl px-6 py-4 font-display uppercase text-lg cm-shadow"
                >
                  <span>📱</span> Compartir en WhatsApp
                </a>
                <button
                  type="button"
                  onClick={() => navigate(
                    demoMode
                      ? `/demo/result/${preResult.debate.debate_id}`
                      : `/result/${preResult.debate.debate_id}`,
                    {
                    replace: true,
                    state: {
                      result: preResult.debate,
                      user,
                      challenge: preResult.challenge,
                      demoMode,
                      seoSlug,
                    },
                  })}
                  className="cm-press w-full bg-white text-[#101010] border-4 border-[#101010] rounded-xl px-6 py-3 font-display uppercase text-base cm-shadow-sm"
                  data-testid="view-full-result-button"
                >
                  Ver resultado completo →
                </button>
              </div>
            </motion.div>
          )}

          {phase === "judging" && (
            <motion.div
              key="judging"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
              data-testid="game-judging"
            >
              <div className="font-display uppercase text-2xl md:text-3xl mb-2">
                REBECA está respondiendo...
              </div>
              <div className="font-mono-cm text-xs uppercase tracking-widest text-[#101010]/60">
                Ronda {currentRound} de 3
              </div>
            </motion.div>
          )}

          {phase === "round_summary" && debate && (
            <motion.div
              key="round_summary"
              initial={{ y: 18, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-5 md:space-y-6"
              data-testid="round-summary"
            >
              <div className="flex items-center gap-3">
                <span className="inline-block bg-[#FF4C4C] text-white border-4 border-[#101010] cm-shadow-sm px-3 py-1 font-mono-cm text-xs uppercase tracking-widest rotate-[-2deg]">
                  Ronda {debate.rounds.length} cerrada
                </span>
                <span className="font-mono-cm text-xs uppercase tracking-widest text-[#101010]/60" data-testid="round-summary-score">
                  Tu puntaje: {debate.rounds[debate.rounds.length - 1]?.score}/100
                </span>
              </div>

              <div className="bg-white border-4 border-[#101010] rounded-xl cm-shadow p-5 md:p-7">
                <div className="flex items-start gap-4">
                  <img
                    src={AI_AVATAR}
                    alt="REBECA"
                    className="w-16 h-16 md:w-20 md:h-20 shrink-0 object-contain"
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-display uppercase text-xl md:text-2xl text-[#FF4C4C]">REBECA</span>
                      <span className="font-mono-cm text-[10px] uppercase tracking-widest bg-[#101010] text-[#FFDE59] px-1.5 py-0.5 rounded">
                        Contraataque
                      </span>
                    </div>
                    <p
                      className="font-medium text-base md:text-lg leading-relaxed"
                      data-testid="round-summary-counter"
                    >
                      {debate.rounds[debate.rounds.length - 1]?.ai_counter}
                    </p>
                  </div>
                </div>
              </div>

              {/* Punto 3: Barra de confianza de REBECA */}
              {runningAvg != null && (
                <div className="border-4 border-[#101010] rounded-xl cm-shadow-sm p-4 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60">
                      Confianza de REBECA
                    </span>
                    <span className="font-mono-cm text-[10px] uppercase tracking-widest font-bold" style={{ color: confidenceColor === "#A8FF76" ? "#2d7a00" : confidenceColor === "#FFDE59" ? "#7a6000" : "#FF4C4C" }}>
                      {confidenceLabel}
                    </span>
                  </div>
                  <div className="w-full bg-[#101010]/10 rounded-full h-4 border-2 border-[#101010] overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: confidenceColor }}
                      initial={{ width: "100%" }}
                      animate={{ width: `${rebecaConfidence}%` }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="font-mono-cm text-[9px] text-[#101010]/40 uppercase">Convencida</span>
                    <span className="font-mono-cm text-[9px] text-[#101010]/40 uppercase">Derrotada</span>
                  </div>
                </div>
              )}

              <div className="bg-[#FFDE59] border-4 border-[#101010] rounded-xl cm-shadow-sm p-4 md:p-5 flex items-start gap-3">
                <span className="font-display text-2xl">⚡</span>
                <p className="text-sm md:text-base font-medium">
                  Tomate el tiempo que necesites para leer. El cronómetro de{" "}
                  <span className="font-display">{roundTimeLimit(debate, debate.current_round)}s</span>{" "}
                  arranca cuando vos digas.
                </p>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={startNextRound}
                  data-testid="start-next-round-button"
                  className="cm-press bg-[#FF4C4C] text-white border-4 border-[#101010] rounded-xl px-7 py-4 font-display uppercase text-lg md:text-xl cm-shadow"
                >
                  Estoy listo → Ronda {debate.current_round}
                </button>
              </div>
            </motion.div>
          )}

          {phase === "playing" && debate && (
            <motion.div
              key={`playing-${currentRound}`}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-5 md:space-y-6"
              data-testid="game-playing"
            >
              <div className="bg-[#FFDE59] border-4 border-[#101010] rounded-xl cm-shadow p-5 md:p-7 flex gap-4 items-start">
                <img
                  src={AI_AVATAR}
                  alt="REBECA"
                  className="w-16 h-16 md:w-20 md:h-20 shrink-0 object-contain"
                />
                <div className="min-w-0">
                  <div className="font-mono-cm text-xs uppercase tracking-widest mb-2 flex items-center gap-2">
                    <span className="text-[#FF4C4C]">REBECA</span> defiende · {debate.category_label}
                  </div>
                  <p
                    className="font-display text-xl md:text-3xl uppercase leading-tight"
                    data-testid="stance-text"
                  >
                    "{debate.stance}"
                  </p>
                </div>
              </div>

              {/* Punto 5: Umbral de victoria + promedio actual */}
              {!isExpressDebate(debate) && (
              <div className="flex items-center gap-3">
                <div className="flex-1 border-2 border-[#101010] rounded-lg bg-white px-3 py-2 flex items-center justify-between">
                  <span className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60">
                    {runningAvg != null ? `Promedio: ${runningAvg}/100` : "Promedio: —"}
                  </span>
                  <span className="font-mono-cm text-[10px] uppercase tracking-widest font-bold text-[#FF4C4C]">
                    META: 75+ para ganar
                  </span>
                </div>
                {runningAvg != null && (
                  <div
                    className="font-display text-lg px-3 py-1.5 border-4 border-[#101010] rounded-lg"
                    style={{ background: runningAvg >= 75 ? "#A8FF76" : runningAvg >= 55 ? "#FFDE59" : "#FF4C4C", color: runningAvg < 55 ? "white" : "#101010" }}
                  >
                    {runningAvg >= 75 ? "Ganando 🔥" : runningAvg >= 55 ? "Parejo ⚡" : "Perdiendo 💀"}
                  </div>
                )}
              </div>
              )}

              {isExpressDebate(debate) && (
                <div className="border-2 border-[#101010] rounded-lg bg-white px-3 py-2 text-center">
                  <span className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/70">
                    Modo express · META 75+ · una sola oportunidad
                  </span>
                </div>
              )}

              {currentRound > 1 && lastCounter && (
                <div className="bg-white border-4 border-[#101010] rounded-xl cm-shadow-sm p-4 md:p-5" data-testid="last-counter">
                  <div className="font-mono-cm text-xs uppercase tracking-widest mb-1.5 text-[#FF4C4C]">
                    REBECA · Ronda {currentRound - 1}
                  </div>
                  <p className="text-sm md:text-base font-medium">{lastCounter}</p>
                </div>
              )}

              <motion.div
                animate={lowTime ? { scale: [1, 1.06, 1] } : { scale: 1 }}
                transition={lowTime ? { duration: 0.6, repeat: Infinity } : {}}
                className={`mx-auto w-full rounded-xl border-4 border-[#101010] cm-shadow flex flex-col items-center justify-center py-5 md:py-6 ${
                  lowTime ? "bg-[#FF4C4C] text-white" : "bg-white"
                }`}
              >
                <div className="font-mono-cm text-xs uppercase tracking-widest mb-1 opacity-80">
                  {roundLabel(debate, currentRound)} · Tiempo restante
                </div>
                <div
                  className="font-mono-cm font-bold text-6xl md:text-8xl tabular-nums leading-none"
                  data-testid="timer-display"
                >
                  {mm}:{ss}
                </div>
              </motion.div>

              <div>
                <label className="font-mono-cm text-xs uppercase tracking-widest mb-2 inline-block">
                  {isExpressDebate(debate)
                    ? "Tu argumento (única oportunidad)"
                    : currentRound === 1
                    ? "Tu primer argumento"
                    : currentRound === 2
                    ? "Tu respuesta al contraataque"
                    : "Tu argumento final"}
                </label>
                <textarea
                  className="cm-textarea w-full p-4 md:p-5 text-lg md:text-xl min-h-[160px] md:min-h-[200px] resize-none"
                  placeholder="Escribí acá..."
                  value={argument}
                  onChange={(e) => setArgument(e.target.value)}
                  maxLength={1500}
                  data-testid="argument-textarea"
                />
                <div className="flex justify-between font-mono-cm text-xs uppercase tracking-widest mt-2 text-[#101010]/60">
                  <span data-testid="argument-counter">{argument.length}/1500</span>
                  <span>{isExpressDebate(debate) || currentRound === debateMaxRounds(debate) ? "Última oportunidad" : "Sé conciso"}</span>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => submitCurrentRound(false)}
                  data-testid="submit-argument-button"
                  className="cm-press bg-[#A8FF76] border-4 border-[#101010] rounded-xl px-7 py-4 font-display uppercase text-lg md:text-xl cm-shadow"
                >
                  {currentRound === 3 ? "Argumento final →" : "Listo →"}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {!demoMode && (
        <ProGateModal
          open={gateOpen}
          onClose={() => setGateOpen(false)}
          onUpgraded={onUpgraded}
          reason={gateReason}
        />
      )}
    </div>
  );
}
